import type {BaseQueryFn} from "@reduxjs/toolkit/query/react";
import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import queryString from "query-string";
import type {KambiDetailsState} from "@atg-sport-shared/sportsbook-types-kambi";
import type {FetchArgs, FetchBaseQueryError} from "@reduxjs/toolkit/query";
import type {EventsResponse, LiveEventsResponse} from "./models";

interface RootState {
    sportsbook: {
        kambiDetails: KambiDetailsState;
    };
}

const getBaseConfig = (state: RootState) => {
    const kambiDetails = state.sportsbook.kambiDetails?.data;

    if (!kambiDetails) {
        throw new Error("Kambi details have not been configured");
    }

    const {offering, kambiOfferingUrl, ...baseParams} = kambiDetails;

    return {
        baseUrl: `${kambiOfferingUrl}/offering/v2018/${offering}`,
        baseParams,
    };
};

const customBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    const state = api.getState() as RootState;
    const {baseUrl, baseParams} = getBaseConfig(state);
    const baseQuery = fetchBaseQuery({baseUrl});

    const normalizedArgs: FetchArgs =
        typeof args === "string"
            ? {url: args, params: baseParams}
            : {...args, params: {...baseParams, ...(args.params || {})}};

    return baseQuery(normalizedArgs, api, extraOptions);
};

export const kambiApi = createApi({
    reducerPath: "kambi-api",
    baseQuery: customBaseQuery,
    endpoints: (builder) => ({
        getLiveEvents: builder.query<LiveEventsResponse, void>({
            query: () => ({
                url: "/event/live/open",
                params: {ncid: Date.now()},
            }),
        }),
        getMainBetoffer: builder.query<EventsResponse, number[]>({
            query: (events) => ({
                url: `/betoffer/selected/main_pre_match_bet_offer/event/${
                    events[0]
                }?${queryString.stringify({
                    ncid: Date.now(),
                    range_start: 0,
                })}${events.slice(1).reduce((acc, event) => `${acc}&id=${event}`, "")}`,
                method: "GET",
            }),
        }),
    }),
});

export const {useLazyGetLiveEventsQuery, useLazyGetMainBetofferQuery} = kambiApi;
